import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FRONTEND_BUILD_INFO, { type FrontendBuildInfo } from '../buildInfo';

const DEFAULT_CHECK_INTERVAL_MS = 5 * 60 * 1000;

interface FrontendVersionManifest {
  version?: string | null;
  frontend_build_commit?: string | null;
  built_at?: string | null;
}

interface UseFrontendVersionGuardOptions {
  enabled?: boolean;
  checkIntervalMs?: number;
}

export interface UseFrontendVersionGuardResult {
  current: FrontendBuildInfo;
  latest: FrontendBuildInfo | null;
  updateAvailable: boolean;
  checking: boolean;
  error: string | null;
  checkNow: () => Promise<void>;
}

function normalizeManifest(payload: FrontendVersionManifest | null | undefined): FrontendBuildInfo | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const version = String(payload.version || '').trim();
  const buildCommit = String(payload.frontend_build_commit || '').trim();
  const builtAt = String(payload.built_at || '').trim();

  if (!version && !buildCommit) {
    return null;
  }

  return {
    version: version || 'unknown',
    buildCommit: buildCommit || 'unknown',
    builtAt: builtAt || '',
  };
}

function manifestsDiffer(current: FrontendBuildInfo, latest: FrontendBuildInfo | null): boolean {
  if (!latest) {
    return false;
  }

  if (
    current.buildCommit !== 'unknown'
    && latest.buildCommit !== 'unknown'
    && latest.buildCommit !== current.buildCommit
  ) {
    return true;
  }

  return latest.version !== current.version;
}

/**
 * Detect whether the frontend container is serving a newer bundle than the one
 * currently loaded in the browser.
 */
export function useFrontendVersionGuard(
  options: UseFrontendVersionGuardOptions = {},
): UseFrontendVersionGuardResult {
  const { enabled = true, checkIntervalMs = DEFAULT_CHECK_INTERVAL_MS } = options;
  const [latest, setLatest] = useState<FrontendBuildInfo | null>(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const checkInFlightRef = useRef<Promise<void> | null>(null);

  const current = FRONTEND_BUILD_INFO;
  const updateAvailable = useMemo(() => manifestsDiffer(current, latest), [current, latest]);

  const checkNow = useCallback(async () => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }
    if (checkInFlightRef.current) {
      return checkInFlightRef.current;
    }

    const run = (async () => {
      setChecking(true);
      try {
        const response = await fetch(`/version.json?ts=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
          },
        });
        if (!response.ok) {
          throw new Error(`version_manifest_http_${response.status}`);
        }
        const payload = normalizeManifest((await response.json()) as FrontendVersionManifest);
        setLatest(payload);
        setError(null);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'version_manifest_failed');
      } finally {
        setChecking(false);
        checkInFlightRef.current = null;
      }
    })();

    checkInFlightRef.current = run;
    return run;
  }, [enabled]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') {
      return;
    }

    void checkNow();

    const intervalId = window.setInterval(() => {
      void checkNow();
    }, checkIntervalMs);

    const handleFocus = () => {
      void checkNow();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkNow();
      }
    };
    const handleOnline = () => {
      void checkNow();
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [checkIntervalMs, checkNow, enabled]);

  return {
    current,
    latest,
    updateAvailable,
    checking,
    error,
    checkNow,
  };
}

export default useFrontendVersionGuard;
